import React, { useState, useEffect } from 'react';
import {
  Box,
  CircularProgress,
  Alert,
  IconButton,
  Button,
  Tooltip,
  Dialog,
} from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { apiService } from '../../services/api.service';
import { InitialsAvatar } from '../ui/InitialsAvatar';
import { EmptyState } from '../ui/EmptyState';
import { ContestFormDialogLayout, FormFooterButtons } from './ContestFormDialogLayout';
import { formatHandle } from '../ui/uiHelpers';

interface Participant {
  userId: string;
  name: string;
  username: string;
  stravaProfile?: {
    firstname?: string;
    lastname?: string;
    profile?: string;
  };
}

interface ContestParticipantsListProps {
  contestId: string;
  onParticipantSelect: (participant: Participant) => void;
  canManageParticipants?: boolean;
  onParticipantRemoved?: () => void;
}

export const ContestParticipantsList: React.FC<ContestParticipantsListProps> = ({
  contestId,
  onParticipantSelect,
  canManageParticipants = false,
  onParticipantRemoved
}) => {
  const { t } = useTranslation('contests');
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [loading, setLoading] = useState(true);
  // Translation key of the current error, resolved at render so it follows language changes.
  const [error, setError] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [participantToDelete, setParticipantToDelete] = useState<Participant | null>(null);
  const [deletingParticipant, setDeletingParticipant] = useState(false);

  useEffect(() => {
    const fetchParticipants = async () => {
      try {
        setLoading(true);
        const participantsList = await apiService.getContestParticipants(contestId);
        setParticipants(participantsList);
      } catch (err) {
        console.error('Error fetching participants:', err);
        setError('participantsList.loadError');
      } finally {
        setLoading(false);
      }
    };

    fetchParticipants();
  }, [contestId]);

  const getDisplayName = (participant: Participant) => {
    if (participant.stravaProfile?.firstname && participant.stravaProfile?.lastname) {
      return `${participant.stravaProfile.firstname} ${participant.stravaProfile.lastname}`;
    }
    return participant.name || participant.username || t('userFallback', { id: participant.userId });
  };

  const handleRemoveParticipant = (participant: Participant) => {
    setParticipantToDelete(participant);
    setDeleteDialogOpen(true);
  };

  const confirmRemoveParticipant = async () => {
    if (!participantToDelete) return;

    try {
      setDeletingParticipant(true);
      await apiService.removeParticipantFromContest(contestId, participantToDelete.userId);
      
      // Remove from local state
      setParticipants(prev => prev.filter(p => p.userId !== participantToDelete.userId));
      
      // Close dialog
      setDeleteDialogOpen(false);
      setParticipantToDelete(null);
      
      // Notify parent component
      if (onParticipantRemoved) {
        onParticipantRemoved();
      }
    } catch (err) {
      console.error('Error removing participant:', err);
      setError('participantsList.removeError');
    } finally {
      setDeletingParticipant(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {t(error)}
      </Alert>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      {participants.length === 0 ? (
        <EmptyState title={t('participantsList.empty')} />
      ) : (
        <Box>
          {participants.map((participant, index) => (
            <Box
              key={participant.userId}
              onClick={() => onParticipantSelect(participant)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.25,
                p: '10px 20px',
                borderTop: index === 0 ? 'none' : '1px solid var(--border)',
                cursor: 'pointer',
                '&:hover': { backgroundColor: 'var(--muted-bg)' },
              }}
            >
              <InitialsAvatar name={getDisplayName(participant)} size={30} />
              <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                <Box component="span" sx={{ fontSize: 14, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {getDisplayName(participant)}
                </Box>
                {formatHandle(participant.username) && (
                  <Box component="span" sx={{ fontSize: 12, color: 'var(--muted)' }}>{formatHandle(participant.username)}</Box>
                )}
              </Box>
              {canManageParticipants && (
                <Tooltip title={t('participantsList.remove')}>
                  <IconButton
                    aria-label={t('participantsList.remove')}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveParticipant(participant);
                    }}
                    size="small"
                    sx={{ color: 'var(--danger)' }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          ))}
        </Box>
      )}

      {/* Delete confirmation dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => !deletingParticipant && setDeleteDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        aria-labelledby="delete-participant-dialog-title"
      >
        <ContestFormDialogLayout
          title={<span id="delete-participant-dialog-title">{t('participantsList.remove')}</span>}
          subtitle={t('participantsList.removeConfirm', { name: participantToDelete ? getDisplayName(participantToDelete) : '' })}
          onClose={() => !deletingParticipant && setDeleteDialogOpen(false)}
          footer={
            <>
              <FormFooterButtons onCancel={() => setDeleteDialogOpen(false)} hideSubmit />
              <Button
                variant="contained"
                color="error"
                onClick={confirmRemoveParticipant}
                disabled={deletingParticipant}
                startIcon={deletingParticipant ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon />}
              >
                {deletingParticipant ? t('participantsList.removing') : t('common:actions.remove')}
              </Button>
            </>
          }
        >
          {null}
        </ContestFormDialogLayout>
      </Dialog>
    </Box>
  );
};
